/* eslint-disable react-hooks/exhaustive-deps */
/**
 * ResultsAsyncEnhanced - assessment progress screen
 * Creates / polls the async eligibility job and hands the result to the
 * results page. Renders the "in progress" and "failed" states only.
 * Spec-Kit canonical ordering: imports → types → constants → helpers → component
 */

// ============================================================================
// IMPORTS
// ============================================================================

// React
import React, { useState, useEffect, useRef } from "react";

// Third-party
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Target, Users, DollarSign, Flag } from "lucide-react";

// First-party components
import { Button } from "../components/ui/button";
import JobMonitor from "../components/JobMonitor";
import ClientOnly from "../components/ClientOnly";
import StarsBackground from "../components/StarsBackground";
import ShootingStars from "../components/ShootingStars";
import SEO from "../components/SEO";

// First-party services & utilities
import { eligibilityAsyncService } from "../services/eligibilityAsyncService.js";
import { logger } from "../utils/logger.js";
import { forceUnlockDocumentScroll } from "../utils/bodyScrollLock";
import { currentRegionHref } from "../lib/regionLocation";
import { getResolvedDashboardEmail } from "../lib/dashboardIdentity";
import { clearAssessmentInProgress } from "../lib/clientGatesStore";

// ============================================================================
// CONSTANTS
// ============================================================================

const TIMEOUT_SECONDS = 300; // 5 minutes timeout for LLM processing
const LONG_RUNNING_THRESHOLD = 120; // 2 minutes before showing "still working" message
const IS_DEV = Boolean(import.meta.env.DEV);
/** Keep users on this screen long enough that the results page never flashes a secondary loader. */
const MIN_MS_ON_LOADING_SCREEN = IS_DEV ? 2500 : 14000;
const SUPPORT_EMAIL = "contact@optra-labs.com";

const formatElapsed = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${String(rest).padStart(2, "0")}`;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if error is a rate limit error (429)
 */
const isRateLimitError = (err) => {
  if (!err) return false;
  if (err.status === 429 || err.code === 429) return true;
  if (err.response?.status === 429) return true;
  const message = typeof err.message === "string" ? err.message : "";
  if (message.includes("429")) return true;
  const stringified = typeof err.toString === "function" ? err.toString() : "";
  return typeof stringified === "string" && stringified.includes("429");
};

/**
 * Check if error indicates job is not ready yet (404)
 */
const isJobNotReadyError = (err) => {
  if (!err) return false;
  const status = err.response?.status ?? err.status ?? err.code;
  if (status === 404) return true;
  const message = typeof err.message === "string" ? err.message : "";
  if (message.includes("404")) return true;
  const stringified = typeof err.toString === "function" ? err.toString() : "";
  return typeof stringified === "string" && stringified.includes("404");
};

/**
 * Calculate polling delay based on poll count
 * Optimized to balance responsiveness and server load, avoiding rate limits
 */
const getPollingDelay = (pollCount) => {
  if (pollCount === 0) {
    return 3000; // Initial delay before first poll: 3 seconds (give backend time to create job)
  } else if (pollCount === 1) {
    return 5000; // First poll: 5 seconds (give backend time to start processing)
  } else if (pollCount <= 5) {
    return 6000; // Polls 2-5: 6 seconds (moderate frequency)
  } else if (pollCount <= 15) {
    return 8000; // Polls 6-15: 8 seconds (reduced frequency)
  } else {
    return 12000; // After 15 polls: 12 seconds (minimal frequency for long-running jobs)
  }
};

const normalizeAssessmentStatus = (status) => String(status || "").trim().toLowerCase();
const isCompletedAssessmentStatus = (status) =>
  ["done", "completed", "complete", "success", "succeeded"].includes(normalizeAssessmentStatus(status));
const isErrorAssessmentStatus = (status) =>
  ["error", "failed", "failure", "cancelled", "canceled"].includes(normalizeAssessmentStatus(status));
const getOutputVetting = (result) => result?.output_vetting || result?.outputVetting || null;
const hasPassedOutputVetting = (result) => {
  const vetting = getOutputVetting(result);
  return String(vetting?.status || "").trim().toLowerCase() === "passed";
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function ResultsAsyncEnhanced({ embedded = false } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [actualJobId, setActualJobId] = useState(searchParams.get("job_id") || location.state?.jobId);
  const jobId = actualJobId;
  const formData = location.state?.formData || {};
  const [traceId] = useState(location.state?.traceId || `trace_enhanced_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const clientId = location.state?.clientId || null;
  /** Friendly, user-facing sentence; technical detail goes to the reference line and the logs. */
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState("");

  const [status, setStatus] = useState("loading");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime] = useState(Date.now());
  const createJobStartedRef = useRef(false);

  const failWith = (message, detail = "") => {
    setError(message);
    setErrorDetail(detail ? String(detail) : "");
    setStatus("error");
  };

  // Structured loading stepper state
  const ANALYSIS_STEPS = [
    "Reviewing company profile",
    "Mapping project objectives",
    "Evaluating budget and feasibility",
    "Checking documentation readiness",
    "Preparing score breakdown",
  ];
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [stepsFinished, setStepsFinished] = useState(false);
  const [jobCompleted, setJobCompleted] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);

  // A failed / timed-out job must not leave the client's gate stuck at
  // "in_progress" (the assessment route would bounce straight back here).
  useEffect(() => {
    if (status !== "error" || !embedded) return;
    clearAssessmentInProgress(getResolvedDashboardEmail());
  }, [status, embedded]);

  // Guard: prevent users from directly visiting /results without coming
  // from the assessment flow. If there is no job_id in the URL and no
  // navigation state indicating a background job creation, redirect back
  // to the dashboard assessment.
  useEffect(() => {
    const hasJobId = Boolean(searchParams.get("job_id"));
    const cameFromChecker = Boolean(location.state?.createJobInBackground || location.state?.jobId);
    if (!hasJobId && !cameFromChecker) {
      navigate("/dashboard/eligibility-assessment", { replace: true });
    }
  }, [searchParams, location.state?.createJobInBackground, location.state?.jobId, navigate]);
  
  // Create job in background if needed (instant navigation pattern)
  useEffect(() => {
    const createJobIfNeeded = async () => {
      if (createJobStartedRef.current) return;
      if (location.state?.createJobInBackground && (!actualJobId || actualJobId === "creating")) {
        createJobStartedRef.current = true;
        try {
          logger.log(`[TRACE-${traceId}] 🚀 Creating job in background after instant navigation`);
          const { jobId: newJobId, traceId: newTraceId } = await eligibilityAsyncService.createJob(
            formData,
            null
          );
          logger.log(`[TRACE-${newTraceId}] ✅ Job created: ${newJobId}`);
          
          // Verify job exists in database before starting to poll
          let jobVerified = false;
          let verificationAttempts = 0;
          const maxVerificationAttempts = 5;
          
          while (!jobVerified && verificationAttempts < maxVerificationAttempts) {
            try {
              await new Promise(resolve => setTimeout(resolve, 1000 + verificationAttempts * 500));
              const status = await eligibilityAsyncService.getJobStatus(newJobId, newTraceId);
              if (status && status.status) {
                logger.log(`[TRACE-${newTraceId}] ✅ Job verified in database: ${newJobId}`);
                jobVerified = true;
              }
            } catch (verifyErr) {
              verificationAttempts++;
              if (isJobNotReadyError(verifyErr)) {
                logger.log(`[TRACE-${newTraceId}] ⏳ Job not yet in database, attempt ${verificationAttempts}/${maxVerificationAttempts}`);
                if (verificationAttempts >= maxVerificationAttempts) {
                  logger.warn(`[TRACE-${newTraceId}] ⚠️ Job ${newJobId} not found after ${maxVerificationAttempts} verification attempts`);
                  // Still proceed - might be a timing issue, polling will handle it
                  jobVerified = true; // Break the loop but proceed
                }
              } else {
                // Non-404 error - something else is wrong
                throw verifyErr;
              }
            }
          }
          
          setActualJobId(newJobId);
          // Update URL without navigation
          // history.replaceState bypasses React Router's basename: add the region prefix.
          window.history.replaceState({}, '', currentRegionHref(`/dashboard/assessment-progress?job_id=${newJobId}`));
        } catch (err) {
          logger.error(`[TRACE-${traceId}] ❌ Failed to create job:`, err);
          if (err?.name === 'AuthRequiredError') {
            // 401 from /start: the backend's own sentence ("Please sign in to run your assessment.").
            failWith(err.message || "Please sign in to run your assessment.");
          } else {
            failWith("We couldn't start the assessment. Your answers are kept, so you can try again straight away.", err?.message);
          }
          createJobStartedRef.current = false;
        }
      }
    };
    
    createJobIfNeeded();
  }, [location.state?.createJobInBackground, actualJobId, formData, traceId]);

  // Leaving /results (e.g. redirect to eligibility-results) must not leave body scroll locked.
  useEffect(() => {
    return () => {
      forceUnlockDocumentScroll();
    };
  }, []);

  // Real-time elapsed timer
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  // Structured loading stepper progression (UI-only timeline)
  useEffect(() => {
    if (stepsFinished) return;
    const totalSteps = ANALYSIS_STEPS.length;
    const intervalMs = IS_DEV ? 900 : 4000; // faster localhost iteration, calmer production pacing

    const id = setInterval(() => {
      setActiveStepIndex((prev) => {
        const next = prev + 1;
        if (next >= totalSteps - 1) {
          clearInterval(id);
          setStepsFinished(true);
          return totalSteps - 1;
        }
        return next;
      });
    }, intervalMs);

    return () => clearInterval(id);
  }, [ANALYSIS_STEPS.length, stepsFinished]);

  // Navigate to results only AFTER backend job done AND stepper finished AND minimum time on this screen
  useEffect(() => {
    if (!jobCompleted || !stepsFinished || !pendingResult) return;

    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_MS_ON_LOADING_SCREEN - elapsed);

    const t = window.setTimeout(() => {
      logger.log(`[TRACE-${traceId}] 🎯 Stepper complete & job done – navigating to EligibilityResults page`);
      forceUnlockDocumentScroll();
      navigate('/dashboard/results', {
        state: {
          assessmentData: pendingResult,
          formData: formData,
          jobId: jobId,
          traceId: traceId,
          clientId,
        },
        replace: true,
      });
    }, remaining);

    return () => window.clearTimeout(t);
  }, [jobCompleted, stepsFinished, pendingResult, navigate, formData, jobId, traceId, clientId, logger, startTime]);

  // Fetch results with proper polling (wait for LLM)
  useEffect(() => {
    if (!jobId || jobId === "creating") {
      setStatus("loading");
      return;
    }

    let isMounted = true;
    let pollCount = 0;
    let rateLimitRetryCount = 0; // Track consecutive rate limit errors

    const fetchResults = async () => {
      if (!isMounted) return;

      try {
        const currentElapsed = Math.floor((Date.now() - startTime) / 1000);
        pollCount++;
        
        logger.log(`[TRACE-${traceId}] 📊 Polling job ${jobId} (${currentElapsed}s elapsed, poll #${pollCount})`);
        logger.log(`[TRACE-${traceId}] 🔍 Calling getJobStatus...`);
        const response = await eligibilityAsyncService.getJobStatus(jobId, traceId);
        
        // Reset rate limit retry count on successful request
        rateLimitRetryCount = 0;
        
        if (!isMounted) return;

        logger.log(`[TRACE-${traceId}] 📈 Status: ${response.status}`);
        logger.debug(`[TRACE-${traceId}] 📦 Full response:`, JSON.stringify(response, null, 2));
        
        if (isCompletedAssessmentStatus(response.status) && response.result) {
          if (!hasPassedOutputVetting(response.result)) {
            logger.error(`[TRACE-${traceId}] ❌ Assessment completed without passed output vetting`, getOutputVetting(response.result));
            failWith(
              "The final checks on this assessment didn't pass, so we haven't shown the results. Please run it again; if it happens twice, contact us with the reference below.",
              `output_vetting=${String(getOutputVetting(response.result)?.status || "unknown")}`
            );
            return;
          }
          logger.log(`[TRACE-${traceId}] ✅ Assessment complete - REAL LLM results received`);
          logger.log(`[TRACE-${traceId}] 📊 Result structure:`, Object.keys(response.result));
          logger.log(`[TRACE-${traceId}] 📊 Grants found: ${response.result.grants?.length || 0}`);
          logger.log(
            `[TRACE-${traceId}] 🛡️ Entity verification present:`,
            Boolean(
              response.result?.entity_verification ||
              response.result?.entityVerification ||
              response.result?.meta?.entity_verification
            )
          );
          logger.debug(`[TRACE-${traceId}] 📊 Full grants:`, response.result.grants);

          // Store results and wait for stepper animation to complete before navigating
          setPendingResult(response.result);
          setJobCompleted(true);
          return; // Stop polling loop; navigation handled by separate effect
        } else if (isErrorAssessmentStatus(response.status)) {
          logger.error(`[TRACE-${traceId}] ❌ Assessment failed:`, response.error);
          if (typeof response.error === "string" && response.error.includes("429")) {
            failWith("We hit a temporary rate limit while checking your eligibility. Please wait a minute and try again.", response.error);
          } else {
            failWith("The assessment could not be completed. Your answers are kept, so you can try again straight away.", response.error);
          }
        } else if (currentElapsed > TIMEOUT_SECONDS) {
          // Increased timeout to 5 minutes (300s) to be more patient with LLM processing
          // LLM assessments typically take 30-90 seconds, but can take longer under load
          logger.error(`[TRACE-${traceId}] ⏰ Timeout after ${TIMEOUT_SECONDS}s - job stuck in ${response.status}`);
          failWith(
            "This is taking longer than expected, so we stopped waiting after 5 minutes. Your answers are kept: try again, or contact us with the reference below.",
            `status=${response.status}`
          );
        } else {
          // Still processing - poll again with adaptive delay
          const delay = getPollingDelay(pollCount);
          logger.log(`[TRACE-${traceId}] ⏳ ${response.status}, polling in ${delay/1000}s (poll #${pollCount})`);
          setTimeout(() => {
            if (isMounted) {
              fetchResults();
            }
          }, delay);
        }
      } catch (err) {
        if (!isMounted) return;

        // Only log non-rate-limit errors to console (429s are expected and handled)
        if (!isRateLimitError(err)) {
          logger.error(`[TRACE-${traceId}] ❌ Fetch error:`, err);
          logger.error(`[TRACE-${traceId}] ❌ Error stack:`, err.stack);
        }
        
        // Send to Sentry (only for non-rate-limit errors to reduce noise)
        if (!isRateLimitError(err)) {
          try {
            const { captureException } = await import('../utils/errorTracking');
            captureException(err, {
              component: 'ResultsAsyncEnhanced',
              action: 'fetchResults',
              jobId,
              traceId,
              pollCount,
              elapsed: Math.floor((Date.now() - startTime) / 1000)
            });
          } catch {
            // Silently fail if Sentry not available
          }
        }
        
        const currentElapsed = Math.floor((Date.now() - startTime) / 1000);

        if (isJobNotReadyError(err)) {
          // Job not found (404) - this can happen if:
          // 1. Job creation is still in progress
          // 2. Job hasn't been saved to database yet
          // 3. Job ID is invalid
          // Use exponential backoff but don't give up too quickly
          const retryDelay = Math.min(3000 + pollCount * 500, 10000);
          logger.log(`[TRACE-${traceId}] ⏳ Job not ready yet (404), retrying in ${retryDelay / 1000}s (poll #${pollCount})`);
          
          // If we've tried many times and still getting 404, the job might not exist
          // Increase threshold to 20 attempts (about 2-3 minutes) to account for slow database writes
          if (pollCount > 20) {
            logger.error(`[TRACE-${traceId}] ❌ Job ${jobId} not found after ${pollCount} attempts (${Math.floor((Date.now() - startTime) / 1000)}s) - job may not exist`);
            failWith(
              "We couldn't find this assessment. It may not have been created properly. Your answers are kept, so you can try again straight away.",
              `job_not_found after ${pollCount} polls`
            );
            return;
          }
          
          setTimeout(() => {
            if (isMounted) {
              fetchResults();
            }
          }, retryDelay);
          return;
        }

        if (isRateLimitError(err)) {
          // Rate limits are expected - implement exponential backoff
          rateLimitRetryCount++;
          const DEBUG = import.meta.env.VITE_DEBUG_LOGS === 'true';
          
          // Exponential backoff: 30s, 60s, 90s (capped at 90s)
          const backoffDelay = Math.min(30000 + (rateLimitRetryCount - 1) * 30000, 90000);
          
          if (DEBUG) {
            logger.log(`[TRACE-${traceId}] ⏳ Rate limit encountered (attempt ${rateLimitRetryCount}), waiting ${backoffDelay/1000}s before retry`);
          }
          
          // Wait longer on rate limit to allow backend to recover
          setTimeout(() => {
            if (isMounted) {
              fetchResults();
            }
          }, backoffDelay);
          return;
        }
        
        // Reset rate limit retry count on non-rate-limit errors
        rateLimitRetryCount = 0;

        if (currentElapsed > TIMEOUT_SECONDS) {
          // Increased timeout to 5 minutes (300s) to match the main timeout
          logger.error(`[TRACE-${traceId}] ❌ Timeout reached after ${TIMEOUT_SECONDS}s, showing error state`);
          failWith(
            "We lost the connection while checking on your assessment and stopped waiting after 5 minutes. Please try again.",
            err?.message
          );
        } else {
          // Retry on network errors with exponential backoff
          const retryDelay = Math.min(5000 + pollCount * 500, 15000);
          logger.log(`[TRACE-${traceId}] ⚠️  Network error, retrying in ${retryDelay/1000}s`);
          setTimeout(() => {
            if (isMounted) {
              fetchResults();
            }
          }, retryDelay);
        }
      }
    };

    // Start polling after initial delay (give backend time to start processing)
    const initialDelay = getPollingDelay(0); // Use same delay logic for consistency
    setTimeout(() => {
      if (isMounted) {
    fetchResults();
      }
    }, initialDelay);

    return () => {
      isMounted = false;
    };
  }, [jobId, traceId, startTime, navigate, formData]);

  const assessmentPath = "/dashboard/eligibility-assessment";
  const progressPath = "/dashboard/assessment-progress";

  /** Re-open the form with the same answers filled in (and skip the in-progress redirect). */
  const handleTryAgain = () => {
    forceUnlockDocumentScroll();
    navigate(`${assessmentPath}?restart=1`, {
      state: { prefillFormData: formData, restart: true, clientId },
      replace: true,
    });
  };

  const referenceLine = [
    traceId ? `Reference ${traceId}` : "",
    jobId && jobId !== "creating" ? `Job ${jobId}` : "",
    errorDetail,
  ]
    .filter(Boolean)
    .join(" · ");

  // Loading state - premium analysis stepper + submission summary
  if (status === "loading") {
    const isLongRunning = elapsedSeconds > LONG_RUNNING_THRESHOLD;
    const allStepsDone = jobCompleted;

    const projectLabel =
      formData?.project_type ||
      formData?.projectType ||
      formData?.projectTitle ||
      formData?.project_title ||
      "Not specified";
    const companySizeLabel =
      formData?.employee_count ||
      formData?.employees ||
      formData?.headcount ||
      "Not specified";
    const budgetLabel = (() => {
      const raw =
        formData?.project_budget_sgd ||
        formData?.project_budget ||
        formData?.budget ||
        null;
      if (!raw && raw !== 0) return "Not specified";
      const num = Number(raw);
      if (Number.isNaN(num)) return String(raw);
      return `S$${num.toLocaleString()}`;
    })();
    const stageLabel =
      formData?.project_maturity ||
      formData?.projectMaturity ||
      formData?.project_stage ||
      "Not specified";
    
    return (
      <div
        className={`relative w-full max-w-full overflow-x-hidden eligibility-results-ui ${
          embedded ? "min-h-[60vh]" : "min-h-screen"
        }`}
        data-testid="eligibility-loading-root"
        data-embedded={embedded ? "true" : undefined}
        style={{
          // Embedded in the dashboard shell: normal flow so the sidebar offset and page
          // scrolling keep working. Standalone: a full-height page.
          background: 'white',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          isolation: 'isolate',
        }}
      >
        <SEO
          title="Assessment in progress | OPTRA"
          description="Your eligibility assessment is being prepared."
          url={progressPath}
          robots="noindex, follow"
        />
        {/* Shooting Stars Background Effect */}
        <ClientOnly>
          <StarsBackground 
            starDensity={0.00015}
            allStarsTwinkle={true}
            twinkleProbability={0.7}
            minTwinkleSpeed={0.5}
            maxTwinkleSpeed={1}
            starColor="#60A5FA"
          />
          <ShootingStars
            minSpeed={10}
            maxSpeed={30}
            minDelay={2000}
            maxDelay={4000}
            starColor="#60A5FA"
            trailColor="#93C5FD"
            starWidth={30}
            starHeight={4}
          />
        </ClientOnly>

        <div className="relative z-20">
          <JobMonitor 
            jobId={jobId}
            traceId={traceId}
            status={status}
            elapsedSeconds={elapsedSeconds}
            pollingCount={0}
          />
          <div className={`flex justify-center relative z-20 ${embedded ? "pt-2 pb-8" : "pt-24 md:pt-28 pb-10 md:pb-12"}`}>
            <div className={`w-full max-w-5xl ${embedded ? "px-0" : "px-4 mt-2 md:mt-4"}`}>
              <div className="bg-white/95 rounded-3xl shadow-2xl border border-[#E5E7EB] px-5 py-7 md:px-8 md:py-8">
                {/* Header */}
                <div className="mb-8 eligibility-loading-copy">
                  <h1
                    className={`${embedded ? "text-[30px]" : "text-3xl md:text-4xl"} font-semibold tracking-tight`}
                    style={{ color: '#60A5FA', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                    data-testid="eligibility-loading-title"
                  >
                    Preparing your eligibility results
                  </h1>
                  <p
                    className="mt-3 text-xs md:text-sm leading-snug max-w-2xl"
                    style={{ color: '#4B5563', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                    data-testid="eligibility-loading-subtitle"
                  >
                    We’re reviewing your business profile, project details, and funding-readiness signals
                    against relevant Singapore grant criteria. Keep this tab open while we prepare your results.
                  </p>
                  <p
                    className="mt-2 text-xs"
                    style={{ color: '#6B7280' }}
                    data-testid="eligibility-elapsed"
                  >
                    Elapsed {formatElapsed(elapsedSeconds)}
                  </p>
                </div>

                {/* Main two-column content */}
                <div className="grid gap-8 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] items-start">
                  {/* Left: analysis stepper (announced as it advances) */}
                  <div className="eligibility-loading-copy" role="status" aria-live="polite" aria-atomic="false">
                    <p className="sr-only">
                      {allStepsDone
                        ? "Assessment complete. Preparing your results."
                        : `Step ${Math.min(activeStepIndex + 1, ANALYSIS_STEPS.length)} of ${ANALYSIS_STEPS.length}: ${ANALYSIS_STEPS[Math.min(activeStepIndex, ANALYSIS_STEPS.length - 1)]}.`}
                    </p>
                    <div className="space-y-5">
                      {ANALYSIS_STEPS.map((label, index) => {
                        const isCompleted = allStepsDone || index < activeStepIndex;
                        const isActive = !allStepsDone && index === activeStepIndex;

                        return (
                          <div key={label} className="flex items-start gap-3">
                            <div className="flex flex-col items-center">
                              <div
                                className={[
                                  "w-6 h-6 rounded-full border flex items-center justify-center transition-colors duration-500 ease-out",
                                  isCompleted
                                    ? "bg-[#60A5FA] border-[#60A5FA]"
                                    : isActive
                                    ? "bg-[#E0F2FE] border-[#60A5FA]"
                                    : "bg-white border-[#E5E7EB]",
                                ].join(" ")}
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full transition-colors duration-500 ease-out"
                                  style={{
                                    backgroundColor: isCompleted
                                      ? "#FFFFFF"
                                      : isActive
                                      ? "#60A5FA"
                                      : "#E5E7EB",
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex-1">
                              <p
                                className="text-sm font-medium"
                                style={{ color: '#111827', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                              >
                                {label}
                              </p>
                              {index === 2 && (
                                <p
                                  className="mt-1 text-xs"
                                  style={{ color: '#6B7280', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                                >
                                  Budget size influences implementation-fit scoring.
                                </p>
                              )}
                              {index === 3 && (
                                <p
                                  className="mt-1 text-xs"
                                  style={{ color: '#6B7280', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                                >
                                  Documentation readiness can improve application viability.
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer microcopy */}
                    <div className="mt-8 space-y-2">
                      {isLongRunning ? (
                        <p
                          className="text-xs md:text-sm leading-snug max-w-2xl"
                          style={{ color: '#4B5563', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                          data-testid="eligibility-long-running"
                        >
                          Still working. Larger assessments can take up to five minutes; we will move you to the
                          results as soon as they are ready, and you can keep this tab open in the meantime.
                        </p>
                      ) : null}
                      <p
                        className="text-xs md:text-sm leading-snug max-w-2xl"
                        style={{ color: '#9CA3AF', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                      >
                        Your results will include an overall eligibility score, factor-by-factor analysis,
                        and tailored recommendations.
                      </p>
                    </div>
                  </div>

                  {/* Right: submission summary card */}
                  <div className="mt-0">
                    <div
                      className="eligibility-loading-copy rounded-2xl border bg-white/90 shadow-lg px-2.5 py-2.5 md:px-3 md:py-3"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <h2
                        className="text-sm font-semibold mb-4"
                        style={{ color: '#111827', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                      >
                        Your submission
                      </h2>
                      <div className="space-y-3">
                        {[
                          {
                            label: 'Project',
                            value: projectLabel,
                            icon: <Target className="w-4 h-4" style={{ color: '#1D4ED8' }} />,
                          },
                          {
                            label: 'Company size',
                            value:
                              companySizeLabel && companySizeLabel !== "Not specified"
                                ? `${companySizeLabel} employees`
                                : 'Not specified',
                            icon: <Users className="w-4 h-4" style={{ color: '#1D4ED8' }} />,
                          },
                          {
                            label: 'Budget',
                            value: budgetLabel,
                            icon: <DollarSign className="w-4 h-4" style={{ color: '#1D4ED8' }} />,
                          },
                          {
                            label: 'Stage',
                            value: stageLabel,
                            icon: <Flag className="w-4 h-4" style={{ color: '#1D4ED8' }} />,
                          },
                        ].map((item) => (
                          <div
                            key={item.label}
                            className="w-full"
                          >
                            <div
                              className="w-full inline-flex items-center gap-2 rounded-full px-3 py-2"
                              style={{
                                backgroundColor: '#EFF6FF',
                                border: '1px solid #DBEAFE',
                              }}
                            >
                              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                                {item.icon}
                              </div>
                              <div className="flex-1 flex flex-col items-start text-left truncate">
                                <span
                                  className="text-[10px] uppercase tracking-wide"
                                  style={{ color: '#6B7280' }}
                                >
                                  {item.label}
                                </span>
                                <span
                                  className="text-xs font-medium truncate"
                                  style={{ color: '#111827' }}
                                >
                                  {item.value || 'Not specified'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state: friendly sentence, a way back into the form with the same answers,
  // and a small reference line for support.
  if (status === "error") {
    return (
      <div
        className={`relative w-full max-w-full overflow-x-hidden eligibility-results-ui ${
          embedded ? "min-h-[60vh]" : "min-h-screen"
        }`}
        data-testid="eligibility-error-root"
        style={{
          background: 'white',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          isolation: 'isolate',
        }}
      >
        <SEO
          title="Assessment could not be completed | OPTRA"
          description="Your eligibility assessment could not be completed."
          url={progressPath}
          robots="noindex, follow"
        />
        <div className="relative z-10">
          <JobMonitor 
            jobId={jobId}
            traceId={traceId}
            status={status}
            elapsedSeconds={elapsedSeconds}
            pollingCount={0}
            error={error}
          />
          <div className={`flex justify-center ${embedded ? "pt-2 pb-8" : "pt-24 md:pt-28 pb-10 md:pb-12"}`}>
            <div className={`w-full max-w-2xl ${embedded ? "px-0" : "px-4"}`}>
              <div
                className="bg-white/95 rounded-3xl shadow-2xl border border-[#E5E7EB] px-5 py-7 md:px-8 md:py-8 eligibility-loading-copy"
                role="alert"
              >
                <AlertCircle className="h-10 w-10 mb-4" style={{ color: '#B91C1C' }} aria-hidden />
                <h1
                  className={`${embedded ? "text-[30px]" : "text-3xl md:text-4xl"} font-semibold tracking-tight`}
                  style={{ color: '#111827' }}
                  data-testid="eligibility-error-title"
                >
                  We couldn't complete your assessment
                </h1>
                <p className="mt-3 text-sm md:text-base leading-relaxed" style={{ color: '#4B5563' }} data-testid="eligibility-error-message">
                  {error || "Something went wrong while processing your eligibility assessment."}
                </p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                  If this keeps happening, email{' '}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Eligibility assessment reference ${traceId}`)}`}
                    className="underline underline-offset-2"
                    style={{ color: '#1d4ed8' }}
                  >
                    {SUPPORT_EMAIL}
                  </a>{' '}
                  and quote the reference below.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    onClick={handleTryAgain}
                    className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Try again
                  </Button>
                </div>
                {referenceLine ? (
                  <p
                    className="mt-6 text-xs break-all"
                    style={{ color: '#6B7280', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    data-testid="eligibility-error-reference"
                  >
                    {referenceLine}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback - should not reach here; reuse loading view instead of a separate spinner
  return null;
}
